/**
 * <PreferenceCardPublicPage> - Iter 9.40 Pass I
 *
 * Canonical public preference-card share page.
 * URL: /preference-share/:cardId
 *
 * Behaviour:
 *  - Vertically centered main card on desktop (pt-12 lg:pt-20)
 *  - Category-specific dual CTAs below (consumer + host) synced from
 *    db.categories.share_cta_copy via GET /api/preference-share/:id/cta
 *  - On mount: POST /track { event_type: "share_view" } (growth loop)
 *  - CTA clicks: POST /track { event_type: "cta_click_consumer" | "cta_click_host" }
 *  - If logged in: "Link experience" and "Group occasion" buttons → LinkExperienceModal
 *  - Eye icon on main card OR any linked card opens AttendeeCardModal
 *  - Any image click opens ImageLightbox
 *  - OG meta tags set dynamically for social preview
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Calendar as CalendarIcon, Loader2, User, Heart, Image as ImageIcon, ArrowRight, Link2, CalendarHeart, Pencil, UserPlus, X } from "lucide-react";
import AttendeeCardModal from "@/components/AttendeeCardModal";
import ImageLightbox from "@/components/ImageLightbox";
import LinkExperienceModal from "@/components/LinkExperienceModal";
import AddToHostClientsModal from "@/components/AddToHostClientsModal";
import OwnerEditShareModal from "@/components/OwnerEditShareModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getSessionId() {
  try {
    let sid = sessionStorage.getItem("share_session_id");
    if (!sid) {
      sid = `ses_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem("share_session_id", sid);
    }
    return sid;
  } catch { return null; }
}

const track = (cardId, eventType, extra = {}) => {
  const isAuthed = !!localStorage.getItem("session_token");
  fetch(`${API}/preference-share/${cardId}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_type: eventType,
      actor_type: isAuthed ? "authed" : "anonymous",
      session_id: getSessionId(),
      referrer: document.referrer || null,
      extra,
    }),
  }).catch(() => {});
};

function InspoStrip({ images, onImageClick, className = "" }) {
  const fills = [0, 1, 2].map((i) => images?.[i] || null);
  return (
    <div className={`grid grid-cols-3 gap-0 ${className}`} data-testid="inspo-strip">
      {fills.map((img, i) =>
        img ? (
          <button key={i} onClick={(e) => { e.stopPropagation(); onImageClick?.(images, i); }} className="overflow-hidden hover:opacity-90 transition-opacity">
            <img src={typeof img === "string" ? img : (img.url || img.image_url)} alt="" className="w-full h-full object-cover cursor-zoom-in" />
          </button>
        ) : (
          <div key={i} className="bg-gray-50 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-gray-200" /></div>
        )
      )}
    </div>
  );
}

function BigCard({ card, onEyeClick, onImageClick }) {
  const inspo = card.inspo_images || card.inspiration_images || [];
  const tags = (card.tags && card.tags.length ? card.tags : Object.values(card.preferences || {}).filter((v) => typeof v === "string" && v && v !== "none")).slice(0, 4);
  return (
    <Card className="overflow-hidden max-w-xl w-full shadow-md" data-testid="preference-share-main">
      <InspoStrip images={inspo} onImageClick={onImageClick} className="h-40" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden shrink-0">
            {card.profile_image ? (
              <img src={card.profile_image} alt={card.name} className="w-full h-full object-cover cursor-zoom-in" onClick={() => onImageClick([card.profile_image], 0)} />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-gray-400" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">{card.name}</h2>
            <p className="text-sm text-gray-400">{card.service_name}</p>
          </div>
          <button onClick={onEyeClick} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="preference-share-eye" aria-label="View full card">
            <Eye className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {tags.map((t, i) => (
            <Badge key={i} variant="secondary" className="rounded-full text-xs font-normal bg-gray-100 text-gray-700 border-0">{t}</Badge>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-1.5 text-sm text-gray-400">
          <CalendarIcon className="w-4 h-4" /> {fmtDate(card.appointment_date)}
        </div>
      </div>
    </Card>
  );
}

function LinkedMini({ card, onEyeClick, onImageClick }) {
  const inspo = card.inspo_images || [];
  const tags = (card.tags || []).slice(0, 2);
  return (
    <Card className="flex-shrink-0 w-52 overflow-hidden" data-testid={`linked-card-${card.rsvp_id}`}>
      <InspoStrip images={inspo} onImageClick={onImageClick} className="h-16" />
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-100 overflow-hidden shrink-0">
            {card.profile_image ? (
              <img src={card.profile_image} alt="" className="w-full h-full object-cover" />
            ) : (<User className="w-3 h-3 text-gray-400 m-auto mt-1.5" />)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate">{card.name}</p>
            <p className="text-[10px] text-gray-400 truncate">{card.service_name}</p>
          </div>
          <button onClick={onEyeClick} className="p-1 rounded hover:bg-gray-100" data-testid={`linked-card-eye-${card.rsvp_id}`}>
            <Eye className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {tags.map((t, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded-full text-[9px] bg-gray-100 text-gray-600">{t}</span>
          ))}
        </div>
      </div>
    </Card>
  );
}

function DualCTA({ cta, cardId, category, isHostMatch, onAddClient }) {
  if (!cta) return null;
  const handleConsumer = () => {
    track(cardId, "cta_click_consumer", { category });
    window.location.href = `/join?intent=consumer&category=${encodeURIComponent(category)}&src=share_card&card_id=${cardId}`;
  };
  const handleHost = () => {
    track(cardId, "cta_click_host", { category });
    window.location.href = `/host/signup?category=${encodeURIComponent(category)}&src=share_card&card_id=${cardId}`;
  };
  return (
    <div className="max-w-xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-3 mt-6" data-testid="dual-cta">
      {/* Consumer CTA */}
      <button
        onClick={handleConsumer}
        className="group text-left p-5 rounded-2xl border border-gray-200 bg-white hover:border-purple-400 hover:shadow-lg transition-all"
        data-testid="consumer-cta"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
            <Heart className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-purple-600">For you</p>
        </div>
        <h3 className="font-bold text-gray-900 text-base leading-snug" data-testid="consumer-cta-headline">
          {cta.consumer_headline}
        </h3>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{cta.consumer_subtext}</p>
        <p className="mt-3 text-sm font-semibold text-purple-700 flex items-center gap-1 group-hover:gap-2 transition-all">
          {cta.consumer_cta_label} <ArrowRight className="w-3.5 h-3.5" />
        </p>
      </button>

      {/* Host CTA - swaps to "Add to my clients" when visitor is a host in matching category */}
      {isHostMatch ? (
        <button
          onClick={onAddClient}
          className="group text-left p-5 rounded-2xl border-2 border-purple-500 bg-gradient-to-br from-purple-600 to-purple-800 text-white hover:shadow-lg transition-all"
          data-testid="host-match-cta"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
              <UserPlus className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-white/90">You're a match</p>
          </div>
          <h3 className="font-bold text-white text-base leading-snug">
            Add this client to my {category} dashboard
          </h3>
          <p className="text-xs text-white/70 mt-1 leading-relaxed">Merge with an existing client or create a new one with preferences pre-filled.</p>
          <p className="mt-3 text-sm font-semibold text-white flex items-center gap-1 group-hover:gap-2 transition-all">
            Add client → <ArrowRight className="w-3.5 h-3.5" />
          </p>
        </button>
      ) : (
        <button
          onClick={handleHost}
          className="group text-left p-5 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-900 to-black text-white hover:shadow-lg transition-all"
          data-testid="host-cta"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-white/70">For hosts</p>
          </div>
          <h3 className="font-bold text-white text-base leading-snug" data-testid="host-cta-headline">
            {cta.host_headline}
          </h3>
          <p className="text-xs text-white/60 mt-1 leading-relaxed">{cta.host_subtext}</p>
          <p className="mt-3 text-sm font-semibold text-white flex items-center gap-1 group-hover:gap-2 transition-all">
            {cta.host_cta_label} <ArrowRight className="w-3.5 h-3.5" />
          </p>
        </button>
      )}
    </div>
  );
}

function OccasionBanner({ occasion, onClick }) {
  if (!occasion) return null;
  return (
    <button
      onClick={onClick}
      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs py-2 px-4 flex items-center gap-2 hover:brightness-110 transition-all"
      data-testid="occasion-banner"
    >
      <CalendarHeart className="w-4 h-4" />
      <span className="font-semibold">Part of {occasion.name}</span>
      {occasion.linked_count > 1 && (
        <span className="opacity-80">· {occasion.linked_count} linked experiences</span>
      )}
      <ArrowRight className="w-3.5 h-3.5 ml-auto" />
    </button>
  );
}

function LoggedInActions({ cardId, category, onOpenLink }) {
  return (
    <div className="max-w-xl w-full mx-auto flex gap-2 mt-4" data-testid="logged-in-actions">
      <Button variant="outline" className="flex-1" onClick={() => onOpenLink("existing")} data-testid="link-existing-btn">
        <Link2 className="w-4 h-4 mr-1.5" /> Link experience
      </Button>
      <Button variant="outline" className="flex-1" onClick={() => onOpenLink("occasion")} data-testid="group-occasion-btn">
        <CalendarHeart className="w-4 h-4 mr-1.5" /> Group under occasion
      </Button>
    </div>
  );
}

export default function PreferenceCardPublicPage() {
  const { cardId } = useParams();
  const [data, setData] = useState(null);
  const [ctaData, setCtaData] = useState(null); // { copy, viewer_labels, occasion, share_override, category_slug, owner_user_id }
  const [loading, setLoading] = useState(true);
  const [modalCard, setModalCard] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [linkModal, setLinkModal] = useState(false);
  const [addClientModal, setAddClientModal] = useState(false);
  const [ownerEditModal, setOwnerEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const isLoggedIn = useMemo(() => !!localStorage.getItem("session_token"), []);

  useEffect(() => {
    if (!cardId) return;
    const auth = isLoggedIn ? { Authorization: `Bearer ${localStorage.getItem("session_token")}` } : {};
    Promise.all([
      fetch(`${API}/preference-share/${cardId}`).then((r) => r.ok ? r.json() : null),
      fetch(`${API}/preference-share/${cardId}/cta`).then((r) => r.ok ? r.json() : null),
      isLoggedIn ? fetch(`${API}/me`, { headers: auth }).then((r) => r.ok ? r.json() : null) : Promise.resolve(null),
    ]).then(([d, c, me]) => {
      setData(d);
      setCtaData(c);
      setCurrentUser(me);
      setLoading(false);
      if (d) track(cardId, "share_view", { category: c?.category_slug });
    }).catch(() => setLoading(false));
  }, [cardId, isLoggedIn]);

  useEffect(() => {
    if (!data) return;
    const cover = data.event_cover_image || (data.inspo_images || [])[0];
    const title = `${data.name || "Client"} · ${data.service_name || "Preference Card"}`;
    const description = `A preference card from ${data.host?.event_name || "Phygital"}. Tap to view.`;
    const setMeta = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    document.title = title;
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("og:image", cover);
    setMeta("og:url", `${window.location.origin}/preference-share/${cardId}`);
    setMeta("twitter:card", "summary_large_image");
  }, [data, cardId]);

  const openLightbox = useCallback((images, i) => {
    setLightbox({
      images: images.map((x) => typeof x === "string" ? x : (x.url || x.image_url)),
      index: i,
      __onChange: (n) => setLightbox((v) => ({ ...v, index: n })),
    });
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Preference card not found.</div>;

  const host = data.host || {};
  const hostName = host.company || host.name || host.first_name;
  const linked = data.linked_cards || [];
  const category = ctaData?.category_slug || host.primary_category || "makeup";
  const viewerLabels = ctaData?.viewer_labels || { hero: "PREFERENCE CARD", subtitle: "What your host sees" };
  const shareOverride = ctaData?.share_override || {};
  const occasion = ctaData?.occasion;
  // derive host-match: current user has primary_category matching card's category
  const isHostMatch = !!(currentUser?.is_provider || currentUser?.pilot_category || currentUser?.role === "provider") &&
    ((currentUser?.pilot_category || "").toLowerCase() === category.toLowerCase());
  const isOwner = currentUser && ctaData?.owner_user_id && currentUser.user_id === ctaData.owner_user_id;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-16" data-testid="preference-card-public-page">
      {/* Occasion banner - topmost */}
      {occasion && <OccasionBanner occasion={occasion} onClick={() => {}} />}

      {/* Tiny header */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-purple-100 overflow-hidden flex items-center justify-center">
            {host.profile_image_url ? (
              <img src={host.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : (<Heart className="w-4 h-4 text-purple-500" />)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold">{viewerLabels.hero}</p>
            <p className="text-sm font-medium truncate">{hostName || host.event_name || "Phygital host"}</p>
          </div>
          {isOwner && (
            <button
              onClick={() => setOwnerEditModal(true)}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-purple-600"
              data-testid="owner-edit-share-btn"
              title="Customize share text"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Centered main card */}
      <main className="px-4 pt-10 md:pt-20 flex flex-col items-center space-y-6">
        <BigCard
          card={data}
          onEyeClick={() => setModalCard({ ...data, __onOpenLinked: (c) => setModalCard(c), __linkedCards: linked })}
          onImageClick={openLightbox}
        />

        {/* Linked cards row */}
        {linked.length > 0 && (
          <section className="max-w-xl w-full" data-testid="linked-row">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 px-1">
              Linked experiences ({linked.length})
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {linked.map((c) => (
                <LinkedMini key={c.rsvp_id} card={c} onEyeClick={() => setModalCard(c)} onImageClick={openLightbox} />
              ))}
            </div>
          </section>
        )}

        {/* Logged-in non-host: Link/Group actions */}
        {isLoggedIn && !isHostMatch && <LoggedInActions cardId={cardId} category={category} onOpenLink={() => setLinkModal(true)} />}

        {/* Dual category-specific CTA - growth loop entry points (CTA swap for hosts in matching category) */}
        <DualCTA
          cta={ctaData?.copy}
          cardId={cardId}
          category={category}
          isHostMatch={isHostMatch}
          onAddClient={() => setAddClientModal(true)}
        />

        <p className="text-[11px] text-gray-400 pt-2 text-center max-w-xl">
          Tap the eye icon for full details. Images tap to expand.
        </p>
      </main>

      <AttendeeCardModal
        open={!!modalCard}
        onOpenChange={(v) => !v && setModalCard(null)}
        rsvp={modalCard}
        hostEventTitle={host.event_name || host.name || "Preference Card"}
        hostAccent="#7c3aed"
        heroLabel={viewerLabels.hero}
        subtitleText={shareOverride.subtitle || viewerLabels.subtitle}
        personalNote={shareOverride.personal_note}
        hideHostControls={true}
      />
      <ImageLightbox value={lightbox} onClose={() => setLightbox(null)} />
      <LinkExperienceModal
        open={linkModal}
        onOpenChange={setLinkModal}
        cardId={cardId}
        category={category}
        onLinked={() => { /* optionally re-fetch */ }}
      />
      <AddToHostClientsModal
        open={addClientModal}
        onOpenChange={setAddClientModal}
        cardId={cardId}
        prefill={{
          name: data.name,
          email: data.email,
          phone: data.phone,
          service_name: data.service_name,
        }}
      />
      <OwnerEditShareModal
        open={ownerEditModal}
        onOpenChange={setOwnerEditModal}
        cardId={cardId}
        initial={shareOverride.subtitle || viewerLabels.subtitle}
        onSaved={(s) => setCtaData((c) => ({ ...c, share_override: { ...(c?.share_override || {}), subtitle: s } }))}
      />
    </div>
  );
}
